import React, { useState } from 'react';
import { Target, Plus, Edit2, Trash2, X, TrendingUp } from 'lucide-react';
import { parseFormattedNumber, formatNumberInput } from '../../utils';

export const ObjectivesTab = ({ weeklyData, weeklyProgress, projectSettings, setProjectSettings, isDark, colors }) => {
  const [objectives, setObjectives] = useState(
    Object.keys(projectSettings).map(key => ({
      id: key,
      name: key,
      ...projectSettings[key]
    }))
  );
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newObjective, setNewObjective] = useState({
    name: '',
    weeklyTarget: '',
    finalTarget: '',
    metricType: 'points',
    customMetricName: '',
    type: 'Trading'
  });

  const TYPE_OPTIONS = [
    { value: 'Trading', label: 'Trading' },
    { value: 'Prediction Markets', label: 'Prediction Markets' },
    { value: 'DeFi', label: 'DeFi' },
    { value: 'NFT', label: 'NFT' },
    { value: 'Social', label: 'Social' },
    { value: 'Gaming', label: 'Gaming' }
  ];

  const METRIC_OPTIONS = [
    { value: 'points', label: 'Points' },
    { value: 'volume', label: 'Volume ($)' },
    { value: 'fees', label: 'Fees Earned ($)' },
    { value: 'custom', label: 'Custom Metric' }
  ];

  const handleNumberChange = (setter, obj, field, value) => {
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setter({ ...obj, [field]: cleanValue });
  };

  const handleNumberBlur = (setter, obj, field) => {
    const value = obj[field];
    if (value) {
      const parsed = parseFormattedNumber(value);
      const formatted = formatNumberInput(parsed);
      setter({ ...obj, [field]: formatted });
    }
  };

  const getMetricLabel = (obj) => {
    if (obj.metricType === 'custom' && obj.customMetricName) {
      return obj.customMetricName;
    }
    const metric = METRIC_OPTIONS.find(m => m.value === obj.metricType);
    return metric ? metric.label : 'Points';
  };

  const getTotalProgress = (projectName, metricType) => {
    let total = 0;
    
    Object.values(weeklyData?.weeks || {}).forEach(weekEntries => {
      weekEntries.forEach(entry => {
        if (entry.project.toLowerCase().includes(projectName.toLowerCase())) {
          if (metricType === 'points') {
            total += entry.points || 0;
          } else if (metricType === 'volume') {
            total += entry.volume || 0;
          } else if (metricType === 'fees') {
            total += entry.fees || entry.cost || 0;
          } else {
            // Custom metric - try to find matching field or use points as fallback
            total += entry[metricType] || entry.points || 0;
          }
        }
      });
    });
    
    return total;
  };

  const getWeeklyProgress = (projectName, metricType) => {
    const progress = weeklyProgress?.[projectName];
    if (!progress) return 0;
    
    if (metricType === 'points') {
      return progress.points || 0;
    } else if (metricType === 'volume') {
      return progress.volume || 0;
    } else if (metricType === 'fees') {
      return progress.fees || progress.costs || 0;
    } else {
      return progress[metricType] || progress.points || 0;
    }
  };

  const addObjective = () => {
    if (!newObjective.name) {
      alert('Please enter a name for the airdrop');
      return;
    }

    const weeklyTargetVal = parseFormattedNumber(newObjective.weeklyTarget);
    const finalTargetVal = parseFormattedNumber(newObjective.finalTarget);

    if (weeklyTargetVal <= 0 && finalTargetVal <= 0) {
      alert('Please enter at least one target (weekly or final)');
      return;
    }

    if (newObjective.metricType === 'custom' && !newObjective.customMetricName) {
      alert('Please enter a name for your custom metric');
      return;
    }

    const objective = {
      weeklyTarget: weeklyTargetVal > 0 ? weeklyTargetVal : null,
      finalTarget: finalTargetVal > 0 ? finalTargetVal : null,
      metricType: newObjective.metricType,
      customMetricName: newObjective.metricType === 'custom' ? newObjective.customMetricName : null,
      type: newObjective.type,
      active: true,
      capital: 0
    };

    setProjectSettings({
      ...projectSettings,
      [newObjective.name]: objective
    });

    setObjectives([...objectives, { id: newObjective.name, name: newObjective.name, ...objective }]);
    setNewObjective({ name: '', weeklyTarget: '', finalTarget: '', metricType: 'points', customMetricName: '', type: 'Trading' });
    setShowAddForm(false);
  };

  const startEdit = (obj) => {
    setEditingId(obj.id);
    setEditForm({
      name: obj.name,
      weeklyTarget: obj.weeklyTarget ? formatNumberInput(obj.weeklyTarget) : '',
      finalTarget: obj.finalTarget ? formatNumberInput(obj.finalTarget) : '',
      metricType: obj.metricType || 'points',
      customMetricName: obj.customMetricName || '',
      type: obj.type
    });
  };

  const saveEdit = () => {
    const weeklyTargetVal = parseFormattedNumber(editForm.weeklyTarget);
    const finalTargetVal = parseFormattedNumber(editForm.finalTarget);

    if (weeklyTargetVal <= 0 && finalTargetVal <= 0) {
      alert('Please enter at least one target');
      return;
    }

    if (editForm.metricType === 'custom' && !editForm.customMetricName) {
      alert('Please enter a name for your custom metric');
      return;
    }

    const updated = {
      ...projectSettings[editingId],
      weeklyTarget: weeklyTargetVal > 0 ? weeklyTargetVal : null,
      finalTarget: finalTargetVal > 0 ? finalTargetVal : null,
      metricType: editForm.metricType,
      customMetricName: editForm.metricType === 'custom' ? editForm.customMetricName : null,
      type: editForm.type
    };

    if (editForm.name !== editingId) {
      const newSettings = { ...projectSettings };
      delete newSettings[editingId];
      newSettings[editForm.name] = updated;
      setProjectSettings(newSettings);

      setObjectives(objectives.map(obj => 
        obj.id === editingId 
          ? { ...obj, id: editForm.name, name: editForm.name, ...updated }
          : obj
      ));
    } else {
      setProjectSettings({
        ...projectSettings,
        [editingId]: updated
      });

      setObjectives(objectives.map(obj => 
        obj.id === editingId ? { ...obj, ...updated } : obj
      ));
    }

    setEditingId(null);
    setEditForm({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const deleteObjective = (id) => {
    if (confirm(`Are you sure you want to delete "${id}"?`)) {
      const newSettings = { ...projectSettings };
      delete newSettings[id];
      setProjectSettings(newSettings);
      setObjectives(objectives.filter(obj => obj.id !== id));
    }
  };

  const inputClasses = `w-full border rounded px-3 py-2 focus:border-blue-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-2xl font-bold flex items-center gap-2 ${colors.textPrimary}`}>
            <Target size={28} className="text-blue-400" />
            Weekly & Final Targets
          </h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Add Target
          </button>
        </div>

        {showAddForm && (
          <div className={`rounded-lg p-5 border mb-6 ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
            <h4 className={`text-lg font-bold mb-4 ${colors.textPrimary}`}>New Target</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Airdrop Name *</label>
                <input
                  type="text"
                  value={newObjective.name}
                  onChange={(e) => setNewObjective({ ...newObjective, name: e.target.value })}
                  placeholder="Hyperliquid"
                  className={inputClasses}
                />
              </div>
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Type</label>
                <select
                  value={newObjective.type}
                  onChange={(e) => setNewObjective({ ...newObjective, type: e.target.value })}
                  className={inputClasses}
                >
                  {TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'}`}>
              <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>📏 Metric to Track</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Metric Type</label>
                  <select
                    value={newObjective.metricType}
                    onChange={(e) => setNewObjective({ ...newObjective, metricType: e.target.value })}
                    className={inputClasses}
                  >
                    {METRIC_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {newObjective.metricType === 'custom' && (
                  <div>
                    <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Custom Metric Name *</label>
                    <input
                      type="text"
                      value={newObjective.customMetricName}
                      onChange={(e) => setNewObjective({ ...newObjective, customMetricName: e.target.value })}
                      placeholder="e.g., LP Fees, Trades, Referrals"
                      className={inputClasses}
                    />
                  </div>
                )}
              </div>
              <p className={`text-xs mt-2 ${colors.textSecondary}`}>
                💡 Choose "Custom Metric" for airdrops that don't use points or volume (e.g., Meteora fees)
              </p>
            </div>

            <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
              <h5 className={`font-semibold mb-3 flex items-center gap-2 ${colors.textPrimary}`}>
                <Target size={18} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                Weekly Target (Optional)
              </h5>
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>
                  Target per Week ({newObjective.metricType === 'custom' ? newObjective.customMetricName || 'units' : METRIC_OPTIONS.find(m => m.value === newObjective.metricType)?.label})
                </label>
                <input
                  type="text"
                  value={newObjective.weeklyTarget}
                  onChange={(e) => handleNumberChange(setNewObjective, newObjective, 'weeklyTarget', e.target.value)}
                  onBlur={() => handleNumberBlur(setNewObjective, newObjective, 'weeklyTarget')}
                  placeholder="1.000"
                  className={inputClasses}
                />
              </div>
            </div>

            <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-green-900/20 border-green-700/30' : 'bg-green-50 border-green-200'}`}>
              <h5 className={`font-semibold mb-3 flex items-center gap-2 ${colors.textPrimary}`}>
                <TrendingUp size={18} className={isDark ? 'text-green-400' : 'text-green-600'} />
                Final Target - Total until TGE (Optional)
              </h5>
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>
                  Total Target ({newObjective.metricType === 'custom' ? newObjective.customMetricName || 'units' : METRIC_OPTIONS.find(m => m.value === newObjective.metricType)?.label})
                </label>
                <input
                  type="text"
                  value={newObjective.finalTarget}
                  onChange={(e) => handleNumberChange(setNewObjective, newObjective, 'finalTarget', e.target.value)}
                  onBlur={() => handleNumberBlur(setNewObjective, newObjective, 'finalTarget')}
                  placeholder="50.000"
                  className={inputClasses}
                />
              </div>
            </div>

            <div className={`text-xs mb-4 ${colors.textSecondary}`}>
              💡 Set weekly, final, or both targets. At least one is required.
            </div>
            <div className="flex gap-2">
              <button
                onClick={addObjective}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewObjective({ name: '', weeklyTarget: '', finalTarget: '', metricType: 'points', customMetricName: '', type: 'Trading' });
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {objectives.length === 0 ? (
          <div className={`text-center py-12 ${colors.textSecondary}`}>
            <Target size={48} className="mx-auto mb-4 opacity-50" />
            <p>No targets yet</p>
            <p className="text-sm mt-2">Click "Add Target" to create your first objective</p>
          </div>
        ) : (
          <div className="space-y-6">
            {objectives.map(obj => {
              const metricType = obj.metricType || 'points';
              const metricLabel = getMetricLabel(obj);
              const weeklyCurrent = getWeeklyProgress(obj.name, metricType);
              const totalCurrent = getTotalProgress(obj.name, metricType);
              const isEditing = editingId === obj.id;

              const weeklyTarget = obj.weeklyTarget;
              const weeklyPercentage = weeklyTarget > 0 ? Math.min((weeklyCurrent / weeklyTarget) * 100, 100) : 0;
              const weeklyComplete = weeklyPercentage >= 100;

              const finalTarget = obj.finalTarget;
              const finalPercentage = finalTarget > 0 ? Math.min((totalCurrent / finalTarget) * 100, 100) : 0;
              const finalComplete = finalPercentage >= 100;

              return (
                <div key={obj.id} className={`rounded-lg p-5 border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  {isEditing ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Name</label>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Type</label>
                          <select
                            value={editForm.type}
                            onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                            className={inputClasses}
                          >
                            {TYPE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'}`}>
                        <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>📏 Metric</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Metric Type</label>
                            <select
                              value={editForm.metricType}
                              onChange={(e) => setEditForm({ ...editForm, metricType: e.target.value })}
                              className={inputClasses}
                            >
                              {METRIC_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          {editForm.metricType === 'custom' && (
                            <div>
                              <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Custom Metric Name</label>
                              <input
                                type="text"
                                value={editForm.customMetricName}
                                onChange={(e) => setEditForm({ ...editForm, customMetricName: e.target.value })}
                                placeholder="e.g., LP Fees"
                                className={inputClasses}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
                        <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>Weekly Target</h5>
                        <input
                          type="text"
                          value={editForm.weeklyTarget}
                          onChange={(e) => handleNumberChange(setEditForm, editForm, 'weeklyTarget', e.target.value)}
                          onBlur={() => handleNumberBlur(setEditForm, editForm, 'weeklyTarget')}
                          placeholder="Leave empty if not needed"
                          className={inputClasses}
                        />
                      </div>

                      <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-green-900/20 border-green-700/30' : 'bg-green-50 border-green-200'}`}>
                        <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>Final Target</h5>
                        <input
                          type="text"
                          value={editForm.finalTarget}
                          onChange={(e) => handleNumberChange(setEditForm, editForm, 'finalTarget', e.target.value)}
                          onBlur={() => handleNumberBlur(setEditForm, editForm, 'finalTarget')}
                          placeholder="Leave empty if not needed"
                          className={inputClasses}
                        />
                      </div>

                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">
                          Save
                        </button>
                        <button onClick={cancelEdit} className={`px-4 py-2 rounded-lg transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className={`text-xl font-bold ${colors.textPrimary}`}>{obj.name}</h4>
                          <div className={`text-sm ${colors.textSecondary}`}>
                            {obj.type} • Tracking: <span className={isDark ? 'text-purple-400' : 'text-purple-600'}>{metricLabel}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => startEdit(obj)} className="text-blue-400 hover:text-blue-300 p-2 transition-colors">
                            <Edit2 size={18} />
                          </button>
                          <button onClick={() => deleteObjective(obj.id)} className="text-red-400 hover:text-red-300 p-2 transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      {weeklyTarget && (
                        <div className={`mb-4 rounded-lg p-4 border ${isDark ? 'bg-blue-900/10 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className={`text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>📊 Weekly Objective</span>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${colors.textPrimary}`}>
                                {weeklyCurrent.toLocaleString()} / {weeklyTarget.toLocaleString()}
                              </div>
                              <div className={`text-xs ${colors.textSecondary}`}>
                                {metricLabel}
                              </div>
                            </div>
                          </div>
                          <div className={`relative h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>
                            <div 
                              className={`absolute h-full transition-all duration-500 ${weeklyComplete ? 'bg-green-500' : 'bg-blue-500'}`} 
                              style={{ width: `${weeklyPercentage}%` }}
                            ></div>
                          </div>
                          <div className="mt-1 text-xs text-right">
                            <span className={weeklyComplete ? 'text-green-400 font-semibold' : colors.textSecondary}>
                              {weeklyPercentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      )}

                      {finalTarget && (
                        <div className={`rounded-lg p-4 border ${isDark ? 'bg-green-900/10 border-green-700/30' : 'bg-green-50 border-green-200'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className={`text-sm font-semibold ${isDark ? 'text-green-300' : 'text-green-600'}`}>🎯 Final Objective (Total)</span>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${colors.textPrimary}`}>
                                {totalCurrent.toLocaleString()} / {finalTarget.toLocaleString()}
                              </div>
                              <div className={`text-xs ${colors.textSecondary}`}>
                                accumulated {metricLabel}
                              </div>
                            </div>
                          </div>
                          <div className={`relative h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>
                            <div 
                              className={`absolute h-full transition-all duration-500 ${finalComplete ? 'bg-green-500' : 'bg-green-600'}`} 
                              style={{ width: `${finalPercentage}%` }}
                            ></div>
                          </div>
                          <div className="mt-1 flex justify-between text-xs">
                            <span className={finalComplete ? 'text-green-400 font-semibold' : colors.textSecondary}>
                              {finalPercentage.toFixed(1)}%
                            </span>
                            {finalComplete && (
                              <span className="text-green-400 font-semibold">✓ Objective completed!</span>
                            )}
                          </div>
                        </div>
                      )}

                      {!weeklyTarget && !finalTarget && (
                        <div className={`text-center py-4 ${colors.textSecondary}`}>
                          <p className="text-sm">No targets set. Click edit to add weekly or final targets.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
